(ns manatee.models.segment
  (:require [clojure.set :as set]
            [manatee.domain.edit :as edit]
            [medley.core :as medley]
            [rothko.editor.document :as document]
            [rothko.editor.document.history.linear :as history]
            [utility-belt.uuid :refer [generate-uuid]]))

(def max-gap-within-segment-in-sec 2)

(def max-gap-between-adjacent-tokens-in-sec 0.75)

(defn round
  [time]
  (-> time
      ;; Working around IEEE 754.
      (* 100)
      (Math/round)
      (/ 100.0)))

(defn token-end
  [{:keys [start duration]}]
  (round (+ start duration)))

(defn remove-speaker-from-token
  [token]
  (dissoc token :speaker))

(defn remove-spaces
  "The transcription comes in without spaces. We are interposing spaces between tokens.
  This means we can remove spaces temporarily for processing."
  [transcript]
  (remove #(= (:type %) :space) transcript))

(defn restore-spaces
  [transcript]
  (interpose {:type :space :word " "} transcript))

(def restore-spaces-v (comp vec restore-spaces))

(defn finalise-transcript
  [transcript]
  (->> transcript restore-spaces (mapv remove-speaker-from-token)))

(defn transcript-within-segment
  [full-transcript segment]
  (let [token-within-segment? (fn [{:keys [start] :as token}]
                                (and (>= start (:start segment))
                                     (<= (token-end token) (:end segment))))
        token-from-expected-speaker? (fn [{:keys [speaker]}]
                                       (or (not (:speaker segment))
                                           (= speaker (:speaker segment))))]
    (->> full-transcript
         remove-spaces
         (filter #(and (token-within-segment? %)
                       (token-from-expected-speaker? %)))
         finalise-transcript)))

(defn tokens-match?
  [a b]
  (let [ks [:type :word :start :duration :confidence]]
    (= (select-keys a ks) (select-keys b ks))))

(defn user-token?
  [{:keys [type system?]}]
  (and (contains? #{:edit :tag} type)
       (not system?)))

(defn update-transcript
  "1. Loop through user’s current transcription and an ‘original’
   (input) transcription inferred from the new segment location/speaker and ML
   data. If ran out of user’s transcription, just add the reminder of
   the ‘original’ to the output. Otherwise, consider head tokens in both
   sequences:

   2. If they are about the same thing, add the user’s token to the output and skip
   over both tokens.

   3. Otherwise, if the user's token has been added manually by them, add it, but
   don't skip over the ‘original’ token.

   4. Otherwise, if any of the remaining ‘original’ tokens is about the same thing
   as the user’s head token, add the ‘original’ head token, but don't skip over
   the user’s token.

   5. Failing that, don't add anything, just skip over the user’s token."
  [user-script original-script]
  (loop [output []
         user-script (remove-spaces user-script)
         original-script (remove-spaces original-script)]
    (cond
      (empty? user-script)
      (-> (into output original-script)
          restore-spaces-v)

      (tokens-match? (first user-script) (first original-script))
      (recur (conj output (first user-script))
             (rest user-script)
             (rest original-script))

      (user-token? (first user-script))
      (recur (conj output (first user-script))
             (rest user-script)
             original-script)

      (some (partial tokens-match? (first user-script)) original-script)
      (recur (conj output (first original-script))
             user-script
             (rest original-script))

      :else
      (recur output
             (rest user-script)
             original-script))))

(defn update-document
  [old-document new-transcript new-original]
  (-> old-document
      (history/append-script new-transcript)
      (assoc :original-script new-original)
      (assoc ::update-key (generate-uuid))))

(defn update-segment-document
  [segment old-document full-transcript]
  (let [new-original (transcript-within-segment full-transcript segment)
        new-transcript (update-transcript (:script old-document) new-original)]
    (cond
      (nil? old-document)
      (-> (document/scripts->document new-original new-original)
          (assoc ::update-key (generate-uuid)))

      (= new-original (:original-script old-document))
      old-document

      :else
      (update-document old-document new-transcript new-original))))

(defn split-segment
  [{:keys [start end speaker] :as segment}
   {:keys [script] {:keys [start-index end-index]} :selection :as document}
   speakers
   full-transcript]
  (let [get-tokens (fn [start end] (-> script (subvec start end) remove-spaces))
        update-segment (fn [tokens]
                         (when (seq tokens)
                           (medley/assoc-some
                            segment
                            :start (some :start tokens)
                            :end (some->> tokens reverse (medley/find-first :start) token-end))))
        new-document (fn [tokens new-segment]
                       (when (seq tokens)
                         (document/scripts->document
                          (restore-spaces-v tokens)
                          (transcript-within-segment full-transcript new-segment))))

        left-tokens (get-tokens 0 start-index)
        selected-tokens (get-tokens start-index (inc end-index))
        right-tokens (get-tokens (inc end-index) (count script))

        left-segment (some-> (update-segment left-tokens) (assoc :start start))
        selected-segment (-> (update-segment selected-tokens)
                             (assoc :speaker (-> (map :value speakers) set (set/difference #{speaker}) first)))
        right-segment (some-> (update-segment right-tokens) (assoc :end end))

        left-document (new-document left-tokens left-segment)
        selected-document (update-document
                           document
                           (restore-spaces-v selected-tokens)
                           (transcript-within-segment full-transcript selected-segment))
        right-document (new-document right-tokens right-segment)]
    {::updated-segment selected-segment
     ::updated-document selected-document
     ::new-segments (remove nil? [left-segment right-segment])
     ::new-documents (remove nil? [left-document right-document])}))

(defn to-segment-sequence
  [segments segment-documents]
  (->> segments
       (medley/map-kv-vals
        (fn [k v]
          (assoc v :transcript (-> segment-documents (get k) :script))))
       (vals)
       (sort-by :start)
       (vec)))

(defn from-segment-sequence
  ([segment-sequence] (from-segment-sequence segment-sequence nil))
  ([segment-sequence full-transcript]
   (let [segments* (zipmap (repeatedly (comp str generate-uuid)) segment-sequence)
         documents (medley/map-vals
                    (fn [segment]
                      (let [transcript (-> segment :transcript edit/coerce-script)]
                        (document/scripts->document
                         transcript
                         (if full-transcript
                           (transcript-within-segment full-transcript segment)
                           transcript))))
                    segments*)
         segments (medley/map-vals
                   (fn [segment] (dissoc segment :transcript))
                   segments*)]
     {::segments segments ::documents documents})))

(defn keep-adjacent-tokens-from-the-same-speaker-together
  "For each token to add, walk the previous result from right to left. If the token
   to add is not sufficiently close to the walked token or not from the same
   speaker, shelve the walked token for later and go left. Otherwise, insert
   between the walked token and shelved tokens."
  [tokens]
  (let [add (fn [to-tokens {:keys [start speaker] :as token}]
              (loop [left to-tokens
                     right nil]
                (let [current (peek left)]
                  (cond
                    (nil? current)
                    (conj to-tokens token)

                    (and (<= (- start (token-end current))
                             max-gap-between-adjacent-tokens-in-sec)
                         (= (:speaker current) speaker))
                    (cond-> (conj left token) right (into right))

                    :else
                    (recur (pop left) (cons current right))))))]
    (reduce add [] tokens)))

(defn transcript->segment-sequence
  "Takes a transcript with speaker in tokens and partitions it into segments by speaker and long gaps."
  [full-transcript]
  (->> full-transcript
       remove-spaces
       keep-adjacent-tokens-from-the-same-speaker-together
       (medley/partition-between
        (fn [a b]
          (let [different-speaker? (not= (:speaker a) (:speaker b))
                gap (- (:start b) (token-end a))
                long-gap? (> gap max-gap-within-segment-in-sec)]
            (or different-speaker? long-gap?))))
       (map
        (fn [[{:keys [start speaker]} :as transcript]]
          (let [transcript' (finalise-transcript transcript)]
            {:start start
             :end (-> transcript' peek token-end)
             :speaker speaker
             :transcript transcript'})))))

(defn from-transcript
  [full-transcript]
  (-> full-transcript transcript->segment-sequence from-segment-sequence))

(defn merge-new
  [{::keys [segments documents] :as old} new-segments new-scripts full-transcript]
  (if (nil? new-segments)
    old
    (let [segment->old-document-id (set/map-invert segments)]
      {::segments
       new-segments
       ::documents
       (medley/map-kv-vals
        (fn [segment-id segment]
          (let [old-document-id (get segment->old-document-id segment)
                old-document (get documents old-document-id)
                new-script (get new-scripts segment-id)
                derived (delay (transcript-within-segment full-transcript segment))]
            (document/scripts->document
             (or new-script
                 (:script old-document)
                 @derived)
             (or (:original-script old-document)
                 @derived))))
        new-segments)})))

(defn time-markers-from-sequence
  [time-markers]
  (zipmap (repeatedly (comp str random-uuid))
          time-markers))

(defn time-markers-to-sequence
  [time-markers]
  (->> time-markers
       vals
       (sort-by (juxt :time-marker-type :time))
       vec))

(defn find-adjacent-segment
  [comparator segments current]
  (when current
    (reduce
     (fn [found {:keys [start] :as candidate}]
       (if (and (comparator start (:start current))
                (or (nil? found)
                    (not (comparator start (:start found)))))
         candidate
         found))
     nil
     (vals segments))))

(defn find-prev-segment-id-by-end-time
  [segments time]
  (->> segments
       (filter (fn [[_ {:keys [end]}]] (< (round end) (round time))))
       (sort-by (comp - :end val))
       ffirst))
